import type { BlogPostMetadata, BlogPost } from '../types/blog';

// This will be populated at build time by scanning the posts directory
const postsContext = import.meta.glob('/src/posts/*/metadata.json', { eager: true });
const contentContext = import.meta.glob('/src/posts/*/content.md', { eager: true, query: '?raw', import: 'default' });
const imagesContext = import.meta.glob('/src/posts/*/*.{png,jpg,jpeg,gif,svg,webp}', { eager: true, import: 'default' });

export async function getAllPosts(): Promise<BlogPostMetadata[]> {
  const posts: BlogPostMetadata[] = [];
  const now = new Date();

  for (const path in postsContext) {
    const metadata = (postsContext[path] as { default: BlogPostMetadata }).default;
    
    // Skip posts with future publish dates
    const publishDate = new Date(metadata.publishedDate);
    if (publishDate > now) {
      continue;
    }
    
    // Create a copy and resolve cover image URL if it exists
    const post = { ...metadata };
    if (post.coverImage) {
      post.coverImage = getPostImageUrl(post.slug, post.coverImage);
    }
    
    posts.push(post);
  }

  // Sort by date, newest first
  return posts.sort((a, b) => {
    return new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime();
  });
}

export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  try {
    const metadataPath = `/src/posts/${slug}/metadata.json`;
    const contentPath = `/src/posts/${slug}/content.md`;

    if (!postsContext[metadataPath] || !contentContext[contentPath]) {
      return null;
    }

    const metadata = (postsContext[metadataPath] as { default: BlogPostMetadata }).default;
    
    // Check if post has a future publish date
    const now = new Date();
    const publishDate = new Date(metadata.publishedDate);
    if (publishDate > now) {
      return null; // Don't show posts scheduled for future
    }
    
    const content = contentContext[contentPath] as string;

    // Resolve cover image URL if it exists
    const coverImage = metadata.coverImage ? getPostImageUrl(slug, metadata.coverImage) : undefined;

    return {
      ...metadata,
      coverImage,
      content,
    };
  } catch (error) {
    console.error(`Error loading post ${slug}:`, error);
    return null;
  }
}

export async function getFeaturedPosts(): Promise<BlogPostMetadata[]> {
  const allPosts = await getAllPosts();
  return allPosts.filter(post => post.featured);
}

export async function getPostsByTag(tag: string): Promise<BlogPostMetadata[]> {
  const allPosts = await getAllPosts();
  return allPosts.filter(post => post.tags.includes(tag));
}

export function getPostImageUrl(slug: string, imageName: string): string {
  // If imageName is already a resolved URL (starts with /assets/ or http), return as is
  if (imageName.startsWith('/assets/') || imageName.startsWith('http') || imageName.startsWith('data:')) {
    return imageName;
  }
  
  const imagePath = `/src/posts/${slug}/${imageName}`;
  
  // Return the imported image URL from Vite's glob import
  if (imagesContext[imagePath]) {
    return imagesContext[imagePath] as string;
  }
  
  // Fallback to constructed path (shouldn't happen if image exists)
  console.warn(`Image not found in import context: ${imagePath}`);
  return imagePath;
}
